import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AuthClient } from '../auth/AuthClient';
import { apiClient } from './client';
/**
 * S-001: `POST /v1/<resource>:batch-delete` returns 207 Multi-Status on a partial failure —
 * axios's default `validateStatus` (2xx only) would otherwise reject a 207 as an error, so
 * every batch-delete mutation below explicitly accepts it alongside 200.
 */
function postBatchDelete(path, ids) {
    return apiClient
        .post(path, { ids }, { validateStatus: (status) => status === 200 || status === 207 })
        .then((r) => r.data);
}
const TASKS_KEY = 'tasks';
const DEFAULT_PAGE_SIZE = 50;
/** Bounds a runaway drain within a single batch; at the server's 200 max limit this is 4000 tasks per batch. */
const MAX_TASK_PAGES = 20;
/**
 * `/v1/tasks` is cursor-paginated. Loop on the cursor, never on
 * `results.length`: with a filter applied, DynamoDB's `Limit` caps items
 * *evaluated* rather than returned, so a page can come back empty (or short)
 * while further matches still exist — keep paging until `targetCount` is
 * reached or the cursor runs out.
 */
async function fetchTaskBatch(params, cursor, targetCount) {
    const results = [];
    let nextCursor = cursor;
    // agentId is sent as the bracketed 'filter[agentId]' query key (per
    // .claude/rules/rest-api-guidelines.md's ?filter[fieldName]=value convention) rather than
    // spread as a flat key alongside the existing status/source/type/owner filters.
    const { agentId, ...restParams } = params;
    const queryParams = { ...restParams, ...(agentId ? { 'filter[agentId]': agentId } : {}) };
    for (let page = 0; page < MAX_TASK_PAGES && results.length < targetCount; page++) {
        const r = await apiClient.get('/v1/tasks', { params: { ...queryParams, cursor: nextCursor } });
        results.push(...r.data.results);
        nextCursor = r.data.pagination.cursor ?? undefined;
        if (!nextCursor)
            break;
    }
    return { results, cursor: nextCursor ?? null };
}
/**
 * Loads tasks a batch at a time (`DEFAULT_PAGE_SIZE` per batch), capping the
 * board at one batch until the caller fetches more via `fetchNextPage`
 * (wired to the board's "Load More" button).
 */
export function useTasks(params = {}) {
    return useInfiniteQuery({
        queryKey: [TASKS_KEY, params],
        queryFn: async ({ pageParam }) => {
            try {
                return await fetchTaskBatch(params, pageParam, DEFAULT_PAGE_SIZE);
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return { results: [], cursor: null };
                }
                throw e;
            }
        },
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
        select: (data) => ({
            results: data.pages.flatMap((p) => p.results),
            pagination: {
                cursor: data.pages[data.pages.length - 1]?.cursor ?? null,
                pageSize: DEFAULT_PAGE_SIZE,
            },
        }),
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
        refetchInterval: (query) => (query.state.status === 'error' ? false : 30000),
    });
}
/**
 * Distinct gitRepoUrl values from the currently loaded github-sourced tasks — no dedicated
 * backend endpoint, since the loaded task set already has everything needed. Originally the
 * tasks board's repo filter (FR-006); reused by the Settings ignored-repos picker and the
 * agent GitHub repos picker so all three "pick a known repo" UIs share one source of truth.
 */
export function useKnownGitRepoUrls() {
    const { data } = useTasks();
    const tasks = data?.results ?? [];
    return Array.from(new Set(tasks
        .filter((t) => t.source === 'github' && t.gitRepoUrl)
        .map((t) => t.gitRepoUrl))).sort();
}
export function useTask(taskId) {
    return useQuery({
        queryKey: [TASKS_KEY, taskId],
        queryFn: async () => {
            try {
                const r = await apiClient.get(`/v1/tasks/${taskId}`);
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    const now = new Date().toISOString();
                    return {
                        taskId,
                        taskType: 'research',
                        source: 'manual',
                        title: 'Start serverless-offline (port 3000) to load this task.',
                        status: 'QUEUED',
                        createdAt: now,
                        updatedAt: now,
                    };
                }
                throw e;
            }
        },
        enabled: !!taskId,
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
    });
}
/** S-006 (FR-007): thin pass-through to GET /v1/analytics/tasks:trends — no client-side
 * reshaping, the backend already returns the exact series/points envelope charts consume. */
export function useAnalyticsTrends(params) {
    return useQuery({
        queryKey: ['analyticsTrends', params],
        queryFn: async () => {
            const r = await apiClient.get('/v1/analytics/tasks:trends', { params });
            return r.data;
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
    });
}
export function useTaskTypes() {
    return useQuery({
        queryKey: ['taskTypes'],
        queryFn: async () => {
            try {
                const r = await apiClient.get('/v1/tasks/types');
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return { types: ['research', 'coding', 'review'] };
                }
                throw e;
            }
        },
        staleTime: Infinity,
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
    });
}
export function usePatchTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ taskId, input }) => apiClient.patch(`/v1/tasks/${taskId}`, input).then((r) => r.data),
        onSuccess: (updated) => {
            qc.setQueryData([TASKS_KEY, updated.taskId], updated);
            qc.invalidateQueries({ queryKey: [TASKS_KEY] });
        },
    });
}
export function useSendTaskAction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ taskId, actionId, message }) => apiClient.post(`/v1/tasks/${taskId}/actions/${actionId}:send`, { message }).then((r) => r.data),
        onSuccess: (updated) => {
            qc.setQueryData([TASKS_KEY, updated.taskId], updated);
        },
        onError: (_error, { taskId }) => {
            // The action may have been marked 'failed' server-side even though this request
            // rejected — refetch so the sidebar reflects the real status instead of silently
            // reverting to the stale 'pending' state with no feedback.
            qc.invalidateQueries({ queryKey: [TASKS_KEY, taskId] });
        },
    });
}
export function useCreateTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => apiClient.post('/v1/tasks', input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [TASKS_KEY] });
        },
    });
}
/** S-001 (FR-001): bulk delete for the tasks board's selection-aware Delete action. */
export function useBatchDeleteTasks() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (ids) => postBatchDelete('/v1/tasks:batch-delete', ids),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [TASKS_KEY] });
        },
    });
}
// --- Schedule hooks ---
const SCHEDULES_KEY = 'schedules';
const EMPTY_SCHEDULES = {
    results: [],
    pagination: { cursor: null, pageSize: 50 },
};
export function useSchedules() {
    return useQuery({
        queryKey: [SCHEDULES_KEY],
        queryFn: async () => {
            try {
                const r = await apiClient.get('/v1/schedules');
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return EMPTY_SCHEDULES;
                }
                throw e;
            }
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
        refetchInterval: (query) => (query.state.status === 'error' ? false : 30000),
    });
}
export function usePatchSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ scheduleId, input }) => apiClient.patch(`/v1/schedules/${scheduleId}`, input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SCHEDULES_KEY] });
        },
    });
}
export function useCreateSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => apiClient.post('/v1/schedules', input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SCHEDULES_KEY] });
        },
    });
}
const SCHEDULE_TASKS_KEY = 'scheduleTasks';
const SCHEDULE_TASKS_LIMIT = 10;
const EMPTY_SCHEDULE_TASKS = {
    results: [],
    pagination: { cursor: null, pageSize: SCHEDULE_TASKS_LIMIT },
};
/**
 * Run history for a schedule's detail sidebar — the most recent `SCHEDULE_TASKS_LIMIT` tasks
 * created by this schedule (`GET /v1/schedules/{scheduleId}/tasks`), newest first. Only fetched
 * while the sidebar for that schedule is open (`enabled`), same as other detail-only hooks.
 */
export function useScheduleTasks(scheduleId) {
    return useQuery({
        queryKey: [SCHEDULE_TASKS_KEY, scheduleId],
        enabled: !!scheduleId,
        queryFn: async () => {
            try {
                const r = await apiClient.get(`/v1/schedules/${scheduleId}/tasks`, {
                    params: { limit: SCHEDULE_TASKS_LIMIT },
                });
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return EMPTY_SCHEDULE_TASKS;
                }
                throw e;
            }
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
    });
}
/** S-001 (FR-001): bulk delete for the schedules board's selection-aware Delete action. */
export function useBatchDeleteSchedules() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (ids) => postBatchDelete('/v1/schedules:batch-delete', ids),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SCHEDULES_KEY] });
        },
    });
}
// --- Agent hooks ---
const AGENTS_KEY = 'agents';
const AGENTS_PAGE_SIZE = 50;
const EMPTY_AGENTS = {
    results: [],
    pagination: { offset: 0, limit: AGENTS_PAGE_SIZE, totalResults: 0, nextOffset: null },
};
/**
 * Loads agents a page at a time (`AGENTS_PAGE_SIZE` per page, offset/limit — matching
 * `GET /v1/agents`'s pagination envelope), capping the board at one page until the caller
 * fetches more via `fetchNextPage` (wired to the board's "Load more" button). Mirrors
 * `useTasks`'s cursor-based infinite-query shape, adapted to offset/limit semantics.
 */
export function useAgents() {
    return useInfiniteQuery({
        queryKey: [AGENTS_KEY],
        queryFn: async ({ pageParam }) => {
            try {
                const r = await apiClient.get('/v1/agents', {
                    params: { offset: pageParam, limit: AGENTS_PAGE_SIZE },
                });
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return EMPTY_AGENTS;
                }
                throw e;
            }
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
        refetchInterval: (query) => (query.state.status === 'error' ? false : 30000),
        staleTime: 10000,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.pagination.nextOffset ?? undefined,
        select: (data) => ({
            results: data.pages.flatMap((p) => p.results),
            pagination: data.pages[data.pages.length - 1]?.pagination ?? EMPTY_AGENTS.pagination,
        }),
    });
}
export function usePatchAgent() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ agentId, input }) => apiClient.patch(`/v1/agents/${agentId}`, input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [AGENTS_KEY] });
        },
    });
}
/** S-001 (FR-002): bulk delete for the agents board's selection-aware Delete action. */
export function useBatchDeleteAgents() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (ids) => postBatchDelete('/v1/agents:batch-delete', ids),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [AGENTS_KEY] });
        },
    });
}
// --- Skill hooks ---
const SKILLS_KEY = 'skills';
const EMPTY_SKILLS = {
    results: [],
    pagination: { cursor: null, pageSize: 50 },
};
export function useSkills() {
    return useQuery({
        queryKey: [SKILLS_KEY],
        queryFn: async () => {
            try {
                const r = await apiClient.get('/v1/skills');
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return EMPTY_SKILLS;
                }
                throw e;
            }
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
    });
}
/** Looks up a skill's display name by ID from a `useSkills()` result — shared by any UI that
 * needs to show which skill a task/schedule is routed through. */
export function findSkillName(skills, skillId) {
    if (!skillId)
        return undefined;
    return skills?.find((s) => s.skillId === skillId)?.name;
}
export function useCreateSkill() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => apiClient.post('/v1/skills', input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
        },
    });
}
export function usePatchSkill() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ skillId, input }) => apiClient.patch(`/v1/skills/${skillId}`, input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
        },
    });
}
/** S-001 (FR-001): bulk delete for the skills board's selection-aware Delete action. */
export function useBatchDeleteSkills() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (ids) => postBatchDelete('/v1/skills:batch-delete', ids),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
        },
    });
}
const EMPTY_SLACK_CHANNELS = { results: [], pagination: { cursor: null, pageSize: 0 } };
/** Backs the Skill admin UI's channel picker — channels the bot has actually joined, via
 * GET /v1/slack/channels (Slack's users.conversations). Failing soft to an empty list keeps
 * the picker usable as a free-text fallback rather than blocking the form. */
export function useSlackChannels() {
    return useQuery({
        queryKey: ['slackChannels'],
        queryFn: async () => {
            try {
                const r = await apiClient.get('/v1/slack/channels');
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return EMPTY_SLACK_CHANNELS;
                }
                throw e;
            }
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
    });
}
// --- Setting hooks ---
const SETTINGS_KEY = 'settings';
const EMPTY_SETTINGS = {
    results: [],
};
export function useSettings() {
    return useQuery({
        queryKey: [SETTINGS_KEY],
        queryFn: async () => {
            try {
                const r = await apiClient.get('/v1/settings');
                return r.data;
            }
            catch (e) {
                if (AuthClient.isMockMode() && isAxiosError(e) && !e.response) {
                    return EMPTY_SETTINGS;
                }
                throw e;
            }
        },
        retry: (failureCount, err) => {
            if (isAxiosError(err) && !err.response)
                return false;
            return failureCount < 1;
        },
        staleTime: 10000,
    });
}
export function usePatchSetting() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ settingKey, input }) => apiClient.patch(`/v1/settings/${settingKey}`, input).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [SETTINGS_KEY] });
        },
    });
}
